import { createContext, useContext, useState, useMemo, useCallback, useRef, ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './JumpLoadingOverlay.scss';
import { CouponData, ActivityCouponResult } from '@/types/coupon-wall';
import { LocationInfo, LocationSource } from "@/lib/location";
import { getUserInfoFromUrl } from '@/lib/login';
import { HeadBanner, FloatButton, ShowCaseInfo } from '@/types/resource-slot';
import { EnhancedSupplyCard } from '@/types/supplyList';
import { isServer } from '@/lib/constant';
import { reportCustomMetric, reportPerfMetric } from '@/lib/owl';
import { getUrlParams } from '@/lib/urlParams';
import { reportLog } from '@/lib/logan';
import recordStep from '@/lib/bridge/recordStep';

/**
 * 用户信息类型
 */
export interface UserInfo {
  userId?: number | null;
  token?: string;
  uuid?: string;
  isLogin?: boolean;
  isLoginExpired?: boolean; // 是否是登录超时，主要是在美小app中，如果是过期，需要先调用退登
  openId?: string;
  openIdCipher?: string;
  isInited?: boolean; // 是否已初始化过用户信息
  [key: string]: any;
}

/**
g * 资源位数据类型
 */
export interface ResourceDataState {
  headBanner: HeadBanner | null;
  floatButton: FloatButton | null;
  showCaseInfo: ShowCaseInfo | null;
}

/**
 * 红包墙优惠券数据类型
 * Context 只存储原始数据，具体的业务转换由组件内部的 transformData 统一处理
 */
export interface CouponDataState {
  // 原始 API 数据，供组件内部 transformData 使用
  rawData: ActivityCouponResult | null;
  
  // SSR 时红包墙接口返回 401 未登录状态
  isNotLoggedIn?: boolean;
  
  // 提前提取 quickScreenCouponList，避免组件内多次转换
  quickScreenCouponList?: CouponData[];
}

/**
 * 供给列表数据类型
 */
export interface SupplyDataState {
  list: EnhancedSupplyCard[];
  totalCount: number;
  hasMore: boolean;
  /** 供给列表接口返回的 requestId，用于分页续传 */
  requestId: string | null;
}

/**
 * Tabbar数据类型（金刚区Tab列表）
 */
export interface TabbarDataState {
  tabList: any[]; // Tab列表
}

/**
 * 限时抢数据类型
 */
export interface LimitedTimeGrabDataState {
  activityList: any[];
  currentTime: number;
  isNotLoggedIn?: boolean;
}

/**
 * Context初始状态类型
 * 用于SSR模式下传递服务端预取的数据
 */
export interface PageStateInitialState {
  resourceData?: ResourceDataState;
  couponData?: CouponDataState;
  supplyData?: SupplyDataState;
  tabbarData?: TabbarDataState; // Tabbar数据（金刚区Tab列表）
  limitedTimeGrabData?: LimitedTimeGrabDataState; // 限时抢数据
  serverUA?: string; // 服务端请求的 UA（用于 SSR 环境检测）
  url?: string; // 传递URL
  isOpenCity?: boolean;
}

/**
 * 页面状态上下文类型
 */
export interface PageStateContextValue {
  // 用户信息（动态）
  userInfo: UserInfo | null;
  setUserInfo: (userInfo: UserInfo | null) => void;

  // 位置信息（动态）
  locationInfo: LocationInfo | null;
  setLocationInfo: (locationInfo: LocationInfo | null) => void;

  // 券召回信息（动态）
  couponRecallInfo: CouponData | null;
  setCouponRecallInfo: (info: CouponData | null) => void;

  // 快筛券列表（动态）
  quickScreenCouponList: CouponData[];
  setQuickScreenCouponList: (list: CouponData[]) => void;

  // 首屏数据状态（用于数据复用检测）
  resourceData: ResourceDataState | null;
  setResourceData: (data: ResourceDataState | null) => void;

  couponData: CouponDataState | null;
  setCouponData: (data: CouponDataState | null) => void;

  supplyData: SupplyDataState | null;
  setSupplyData: (data: SupplyDataState | null) => void;

  // Tabbar数据（金刚区Tab列表）
  tabbarData: TabbarDataState | null;
  setTabbarData: (data: TabbarDataState | null) => void;

  // 限时抢数据
  limitedTimeGrabData: LimitedTimeGrabDataState | null;
  setLimitedTimeGrabData: (data: LimitedTimeGrabDataState | null) => void;

  // 服务端 UA（用于 SSR 环境检测）
  serverUA: string | undefined;

  // 标记模块加载状态
  setModuleLoaded: (moduleName: string) => void;

  // 是否获取完unpl。未取完不允许跳转
  // 1.搜索；2.团购商卡、外卖商卡；3.瓷片；4.红包去使用按钮；
  completeGetUnpl: Boolean;
  setCompleteGetUnpl: (completeGetUnpl: boolean) => void;
  /**
   * 等待 completeGetUnpl 变为 true 的 Promise。
   * - 若当前已为 true，返回立即 resolve 的 Promise
   * - 否则返回一个在 setCompleteGetUnpl(true) 时 resolve 的 Promise
   */
  waitForUnpl: () => Promise<void>;

  // 全局跳转 Loading 状态（等待 sessionId 绑定时展示）
  jumpLoading: boolean;
  setJumpLoading: (loading: boolean) => void;

  isOpenCity: boolean;
  setIsOpenCity: (isOpenCity: boolean) => void;

  url: string | undefined;
}

// 创建 Context
const PageStateContext = createContext<PageStateContextValue | null>(null);

/**
 * 页面状态 Provider
 */
export function PageStateProvider({
  children,
  initialLocationInfo,
  initialState, // 新增参数
  openCityIdList,
}: {
  children: ReactNode;
  initialLocationInfo?: LocationInfo | null;
  initialState?: PageStateInitialState; // 新增类型
  openCityIdList?: number[];
}) {
  // 在组件内部调用 getUserInfoFromUrl
  // useState 的初始值只在首次渲染时计算，所以不需要 useMemo 缓存
  const [userInfo, setUserInfo] = useState<UserInfo | null>(() => getUserInfoFromUrl() || null);
  const [locationInfoRaw, setLocationInfoRaw] = useState<LocationInfo | null>(initialLocationInfo || null);
  const [serverUA] = useState<string | undefined>(() => initialState?.serverUA); // 服务端 UA
  const [isOpenCity, setIsOpenCity] = useState<boolean>(initialState?.isOpenCity ?? true);
  const moduleLoadedRecordRef = useRef<Record<string, boolean>>({
    couponWall: false,
    tabbar: false,
    supplyList: false,
    resource: false,
  });
  const locationSourceRef = useRef<LocationSource>(LocationSource.DEFAULT);

  const setLocationInfo = useCallback((newLocationInfo: LocationInfo | null) => {
    setIsOpenCity(openCityIdList ? openCityIdList.includes(newLocationInfo?.selectCityInfo?.cityId || 0) : true);
    setLocationInfoRaw(newLocationInfo);
    locationSourceRef.current = newLocationInfo?.source || LocationSource.DEFAULT;
  }, []);
  
  const [couponRecallInfo, setCouponRecallInfo] = useState<CouponData | null>(null);
  
  const [quickScreenCouponList, setQuickScreenCouponList] = useState<CouponData[]>(
    () => initialState?.couponData?.quickScreenCouponList || []
  );
  const [completeGetUnpl, setCompleteGetUnpl] = useState<Boolean>(true);
  const [jumpLoading, setJumpLoading] = useState<boolean>(false);

  // 共享的「等待 unpl 完成」Promise，所有跳转等待的是同一个实例
  // completeGetUnpl 为 true 时为 null（直接放行），为 false 时持有一个 pending Promise
  const unplPromiseRef = useRef<Promise<void> | null>(null);
  const unplResolveRef = useRef<(() => void) | null>(null);

  const waitForUnpl = useCallback((): Promise<void> => {
    if (completeGetUnpl) return Promise.resolve();
    // 复用同一个 Promise，所有跳转共享等待
    if (!unplPromiseRef.current) {
      unplPromiseRef.current = new Promise<void>((resolve) => {
        unplResolveRef.current = resolve;
      });
    }
    return unplPromiseRef.current;
  }, [completeGetUnpl]);

  // 包装 setCompleteGetUnpl，变为 true 时 resolve 共享 Promise 并重置
  const setCompleteGetUnplWithRef = useCallback((value: boolean) => {
    setCompleteGetUnpl(value);
    if (value) {
      unplResolveRef.current?.();
      unplPromiseRef.current = null;
      unplResolveRef.current = null;
    }
  }, []);

  // 首屏数据状态初始化 - 支持从initialState初始化
  const [resourceData, setResourceData] = useState<ResourceDataState | null>(
    () => initialState?.resourceData || null  // 使用initialState
  );
  const [couponData, setCouponData] = useState<CouponDataState | null>(
    () => initialState?.couponData || null  // 使用initialState
  );
  const [supplyData, setSupplyData] = useState<SupplyDataState | null>(
    () => initialState?.supplyData || null  // 使用initialState
  );
  // Tabbar数据（金刚区Tab列表）
  const [tabbarData, setTabbarData] = useState<TabbarDataState | null>(
    () => initialState?.tabbarData || null  // 使用initialState
  );

  // 限时抢数据初始化
  const [limitedTimeGrabData, setLimitedTimeGrabData] = useState<LimitedTimeGrabDataState | null>(
    () => initialState?.limitedTimeGrabData || null
  );

  const [url, setUrl] = useState<string | undefined>(() => initialState?.url);

  const setModuleLoaded = useCallback((moduleName: keyof typeof moduleLoadedRecordRef.current) => {
    if (isServer) {
      return;
    }
    if (moduleLoadedRecordRef.current[moduleName]) {
      return;
    }
    moduleLoadedRecordRef.current[moduleName] = true;
    // 上报接口数据返回埋点
    recordStep({ stepName: `web_action_data_empty_${moduleName}` });

    // 所有模块加载完成后，上报页面加载完毕，统计用户留存率使用
    if (Object.values(moduleLoadedRecordRef.current).every((loaded) => loaded)) {
      const tti = Date.now();
      // SSR 框架性能埋点上报：CSR 首屏 TTI 时间
      if ((window as any)?.reportOwlCustomMetric) {
        (window as any).reportOwlCustomMetric("web.csr.tti");
      }
      // 测速埋点
      if((window as any)?.sendPerfPoint) {
        (window as any).sendPerfPoint({
          name: "web.csr.tti",
          timeStamp: tti,
          position: 15
        })
      }
      reportCustomMetric({
        key: "pageLoadedCount",
        value: 1,
      })

      // 上报 CSR 页面加载耗时：
      // 1. SSR 场景：起点为 navigationStart，终点为 window.__PAGE_LVC_TIME__
      // 2. 经过 click 页的 CSR 场景：起点为 click 页面传递的 navigationStart，终点为 TTI
      // 3. 中间件返回 click HTML 的 CSR 场景：起点为 click 页面传递的 navigationStart，终点为 TTI
      if (!initialState) {
        // initialState 不存在时，说明是 CSR 的场景
        // 所有模块加载完成上报加载时间
        reportPerfMetric({
          key: "pageLoadFinished",
          value: tti - ((window as any)?.__PAGE_JS_START__ || 0),
          tags: {
            locationSource: locationSourceRef.current,
          }
        })

        const urlParams = getUrlParams();
        const navigationStartFromUrl = Number(urlParams.navigationStart);


        if (navigationStartFromUrl) {
          reportPerfMetric({
            key: 'pageLoadFinished_CSRFromClick',
            value: tti - navigationStartFromUrl,
            tags: {
              locationSource: locationSourceRef.current,
            } 
          })
        }
        reportPerfMetric({
          key: 'pageLoadFinished_CSR',
          value: tti - performance.timeOrigin,
          tags: {
            locationSource: locationSourceRef.current,
          } 
        })

        // 上报 CSR 页面加载结束的时间戳
        if (urlParams.rcf_uniqueid) {
          reportPerfMetric({
            key: 'pageLoadFinishedTime_CSR',
            value: tti,
          })
        }
      }
      // 上报日志
      reportLog();

      // 上报SSR-SDK收集埋点
      if (typeof (window as any).reportPointCustomSpeed === 'function') {
        (window as any).reportPointCustomSpeed();
      }
    }
  }, []);

  // 使用 useMemo 避免不必要的重渲染
  const value = useMemo<PageStateContextValue>(
    () => ({
      userInfo,
      setUserInfo,
      locationInfo: locationInfoRaw,
      setLocationInfo,
      couponRecallInfo,
      setCouponRecallInfo,
      quickScreenCouponList,
      setQuickScreenCouponList,
      // 首屏数据状态
      resourceData,
      setResourceData,
      couponData,
      setCouponData,
      supplyData,
      setSupplyData,
      // Tabbar数据
      tabbarData,
      setTabbarData,
      // 限时抢数据
      limitedTimeGrabData,
      setLimitedTimeGrabData,
      // 服务端 UA
      serverUA,
      setModuleLoaded,
      completeGetUnpl,
      setCompleteGetUnpl: setCompleteGetUnplWithRef,
      waitForUnpl,
      jumpLoading,
      setJumpLoading,
      url,
      isOpenCity,
      setIsOpenCity,
    }),
    [userInfo, locationInfoRaw, setLocationInfo, couponRecallInfo, quickScreenCouponList, tabbarData, limitedTimeGrabData, serverUA, setModuleLoaded, completeGetUnpl, setCompleteGetUnplWithRef, waitForUnpl, jumpLoading, setJumpLoading, url, isOpenCity, setIsOpenCity]
  );

  // 确保只在客户端挂载后才渲染 Portal，避免 hydration mismatch
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  return (
    <PageStateContext.Provider value={value}>
      {children}
      {/* 全局跳转 Loading：等待 sessionId 绑定时展示 */}
      {mounted && jumpLoading && createPortal(
        <div className="jump-loading-overlay">
          <div className="jump-loading-box">
            <div className="jump-loading-spinner" />
          </div>
        </div>,
        document.body
      )}
    </PageStateContext.Provider>
  );
}

/**
 * 使用页面状态的 Hook
 */
export function usePageState() {
  const context = useContext(PageStateContext);
  if (!context) {
    throw new Error("usePageState must be used within PageStateProvider");
  }
  return context;
}

/**
 * 只获取用户信息的 Hook
 */
export function useUserInfo() {
  const { userInfo, setUserInfo } = usePageState();
  return { userInfo, setUserInfo };
}

/**
 * 只获取位置信息的 Hook
 */
export function useLocationInfo() {
  const { locationInfo, setLocationInfo } = usePageState();
  return { locationInfo, setLocationInfo };
}

/**
 * 只获取券召回信息的 Hook
 */
export function useCouponRecallInfo() {
  const { couponRecallInfo, setCouponRecallInfo } = usePageState();
  return { couponRecallInfo, setCouponRecallInfo };
}

/**
 * 只获取快筛券列表的 Hook
 */
export function useQuickScreenCouponList() {
  const { quickScreenCouponList, setQuickScreenCouponList } = usePageState();
  return { quickScreenCouponList, setQuickScreenCouponList };
}

/**
 * 只获取资源位数据的 Hook
 */
export function useResourceData() {
  const { resourceData, setResourceData } = usePageState();
  return { resourceData, setResourceData };
}

/**
 * 只获取优惠券数据的 Hook
 */
export function useCouponData() {
  const { couponData, setCouponData } = usePageState();
  return { couponData, setCouponData };
}

/**
 * 只获取供给列表数据的 Hook
 */
export function useSupplyData() {
  const { supplyData, setSupplyData } = usePageState();
  return { supplyData, setSupplyData };
}

/**
 * 只获取Tabbar数据的 Hook
 */
export function useTabbarData() {
  const { tabbarData, setTabbarData } = usePageState();
  return { tabbarData, setTabbarData };
}

/**
 * 只获取限时抢数据的 Hook
 */
export function useLimitedTimeGrabData() {
  const { limitedTimeGrabData, setLimitedTimeGrabData } = usePageState();
  return { limitedTimeGrabData, setLimitedTimeGrabData };
}

/**
 * 只获取模块加载状态的 Hook
 */
export function useReportPageLoaded() {
  const { setModuleLoaded } = usePageState();
  return { setModuleLoaded };
}

/**
 * 获取服务端 UA 的 Hook
 * 用于 SSR 环境检测，优先使用服务端传入的 UA，客户端时返回 undefined
 */
export function useServerUA() {
  const { serverUA } = usePageState();
  return serverUA;
}

export function useIsOpenCity() {
  const { isOpenCity, setIsOpenCity } = usePageState();
  return { isOpenCity, setIsOpenCity };
}
export default PageStateContext;